// PlaybookDraftView.jsx — "3. Draft ready" view (POST /playbook/{uid}/generate response)
import React, { useState, useRef, useEffect } from "react";
import {
  editSection,
  explainTerm,
  improveDraft,
  setDraftStatus,
  requestReview,
  cancelReview,
  markReviewSeen,
} from "./playbookApi";

const STATUS_TONE = {
  PASS: "ok",
  FAIL: "danger",
  WARN: "warn",
  REQUIRED: "strong",
  NA: "muted",
};

// "[DETAIL REQUIRED: …]" placeholders-ai red-la highlight
const PH_RE = /(\[DETAIL REQUIRED:[^\]]*\])/g;
const withPlaceholders = (text) =>
  String(text ?? "")
    .split(PH_RE)
    .map((part, i) =>
      part.startsWith("[DETAIL REQUIRED") ? (
        <span key={i} className="pbp-ph">{part}</span>
      ) : (
        <React.Fragment key={i}>{part}</React.Fragment>
      )
    );

// "2 Nov, 1:48 pm"
const fmtShort = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d)
    ? ""
    : d.toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit", hour12: true });
};

const fmtWhen = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d)
    ? ""
    : d.toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

// review_request.status → card tone + fallback label
const REVIEW_STATE = {
  requested: { tone: "wait", label: "Waiting for advocate" },
  in_review: { tone: "wait", label: "Advocate is reviewing" },
  approved: { tone: "ok", label: "Approved by advocate" },
  changes_requested: { tone: "bad", label: "Advocate asked for changes" },
  rejected: { tone: "bad", label: "Advocate rejected the draft" },
};

const LOG_TEXT = {
  requested: "Sent to advocate",
  cancelled: "Request cancelled",
  approved: "Approved",
  changes_requested: "Changes requested",
  rejected: "Rejected",
  seen: "Seen",
};

const sourceText = (f) =>
  f?.source_type === "uploaded_document"
    ? `${f.source_document || "document"}${f.page ? `, p. ${f.page}` : ""}`
    : "you";

const PlaybookDraftView = ({ userId, draft, draftId, onDraftChange, onBackToFacts, notify }) => {
  const [showTerms, setShowTerms] = useState(false);
  const [termInfo, setTermInfo] = useState(null); // { term, text, loading }
  const [openCheck, setOpenCheck] = useState(null);
  const [editKey, setEditKey] = useState(null);
  const [editText, setEditText] = useState("");
  const [saving, setSaving] = useState(false);
  const docRef = useRef(null);
  const [tool, setTool] = useState(null); // "improve" | "final" | "send" | "opponent" | "judgments" | null
  const [busy, setBusy] = useState(null);
  const [improveText, setImproveText] = useState("");
  const [reviewNote, setReviewNote] = useState("");
  const [applyReview, setApplyReview] = useState(true);
  const [approval, setApproval] = useState({ name: "", enrolment: "", note: "", confirm: false });

  const v = draft.validation || {};
  const dashboard = draft.dashboard || [];
  const conf = v.confidence || {};
  const terms = v.terms || draft.terms || [];
  const reviewItems = v.review_items || [];
  const sections = draft.sections || [];
  const factsUsed = draft.facts_used || [];
  const statutes = draft.statutes || [];
  const review = draft.review || {};

  const statusTone = (s) => {
    const t = String(v.status || "").toUpperCase();
    return t === "PASS" || t === "READY" ? "ok" : t === "FAIL" ? "danger" : "warn";
  };

  // ── terms ────────────────────────────────────────────────────────────────
  // GET /drafts/{id}/terms/{term} →
  //   { term, category, category_label, meaning, plain_alternative, same_effect_in_plain_words, reason_used }
  // Register-la illadha term-ku (not_in_register) backend 404 tharum → draft-la irukkura info kaattu.
  const CATEGORY_LABELS = {
    essential: "Essential legal term",
    not_in_register: "Not in the terms register",
  };

  const openTerm = async (t) => {
    if (termInfo?.term === t.term) return setTermInfo(null);
    const local = {
      term: t.term,
      label: CATEGORY_LABELS[t.category] || t.category,
      meaning: t.meaning,
      plain: t.plain,
      why: t.reason_used,
    };
    // Response varra varaikkum Loading mattum — response vandha piragu dhaan details
    setTermInfo({ term: t.term, loading: true });
    try {
      const res = await explainTerm(userId, draftId, t.term);
      setTermInfo((cur) =>
        cur?.term !== t.term
          ? cur
          : {
              term: res?.term || t.term,
              label: res?.category_label || local.label,
              meaning: res?.meaning || local.meaning,
              plain: res?.same_effect_in_plain_words === false ? null : res?.plain_alternative || local.plain,
              why: res?.reason_used || local.why,
              note: res?.note || null,
              loading: false,
            }
      );
    } catch (e) {
      setTermInfo((cur) =>
        cur?.term !== t.term
          ? cur
          : {
              ...local,
              loading: false,
              missing: e.status === 404,
              error: e.status === 404 ? null : e.message || "Could not explain this term.",
            }
      );
    }
  };

  // ── section edit ─────────────────────────────────────────────────────────
  const startEdit = (s) => {
    setEditKey(s.key);
    setEditText((s.paragraphs || []).join("\n\n"));
  };

  const saveEdit = async () => {
    const paragraphs = editText
      .split(/\n\s*\n/)
      .map((p) => p.trim())
      .filter(Boolean);
    setSaving(true);
    try {
      const res = await editSection(userId, draftId, editKey, { paragraphs, version: draft.version });
      if (res && Array.isArray(res.sections)) onDraftChange(res);
      else
        onDraftChange({
          ...draft,
          sections: sections.map((s) => (s.key === editKey ? { ...s, paragraphs, edited_by_user: true } : s)),
        });
      setEditKey(null);
      notify("success", "Section updated.");
    } catch (e) {
      notify("error", e.message || "Could not save the section.");
    } finally {
      setSaving(false);
    }
  };

  // ── advocate review (POST /review response → review_request, review_flag) ──
  const rr = draft.review_request;
  const flag = draft.review_flag;
  const rState = rr && rr.status !== "cancelled" ? REVIEW_STATE[rr.status] || { tone: "wait", label: rr.status } : null;
  const [showLog, setShowLog] = useState(false);

  // Advocate decision vandhu user paakalana (user_unseen) → seen nu mark pannu
  useEffect(() => {
    if (rr?.user_unseen && draftId) markReviewSeen(userId, draftId).catch(() => {});
  }, [rr?.user_unseen, draftId, userId]);

  // ── draft actions ────────────────────────────────────────────────────────
  const acts = draft.actions || {};
  const can = (k) => acts[k] === undefined || !!acts[k];

  // Response full draft-aa vandha replace; illana status/approval mattum merge
  const applyResult = (res, fallback) => {
    if (res && Array.isArray(res.sections)) onDraftChange(res);
    else if (res && typeof res === "object" && !Array.isArray(res))
      onDraftChange({ ...draft, ...fallback, ...res, sections: draft.sections });
    else onDraftChange({ ...draft, ...fallback });
  };

  const act = async (tag, fn, okMsg, fallback) => {
    setBusy(tag);
    try {
      const res = await fn();
      applyResult(res, fallback);
      if (okMsg) notify("success", okMsg);
      return true;
    } catch (e) {
      if (/DETAIL REQUIRED|placeholder/i.test(e.message || "")) {
        const n = parseInt(e.message, 10);
        warnPlaceholders(Number.isFinite(n) ? n : placeholderCount || 1);
      } else {
        notify(
          "error",
          e.status === 409 ? "This draft changed elsewhere — close and reopen the panel, then try again." : e.message || "Something went wrong."
        );
      }
      return false;
    } finally {
      setBusy(null);
    }
  };

  // POST /improve { instructions, apply_review, expected_version } → new draft version
  const submitImprove = async () => {
    const instructions = improveText.trim();
    if (!instructions) return;
    const ok = await act(
      "improve",
      () => improveDraft(userId, draftId, { instructions, applyReview, version: draft.version }),
      "Draft improved."
    );
    if (ok) {
      setImproveText("");
      setTool(null);
    }
  };

  // Draft-la innum irukkura [DETAIL REQUIRED] placeholders
  const placeholderCount = sections.reduce(
    (n, sec) => n + (sec.paragraphs || []).reduce((m, p) => m + (String(p).match(/\[DETAIL REQUIRED/g) || []).length, 0),
    0
  );

  // Muthal placeholder-ku scroll panni konja neram highlight
  const showFirstPlaceholder = () => {
    const el = docRef.current?.querySelector(".pbp-ph");
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("pbp-ph-flash");
    setTimeout(() => el.classList.remove("pbp-ph-flash"), 2200);
  };

  const warnPlaceholders = (count) => {
    notify(
      "warning",
      `Fill the required details in the draft before approving — ${count} [DETAIL REQUIRED] item${count === 1 ? "" : "s"} left. Use Edit on each section.`
    );
    showFirstPlaceholder();
  };

  // PATCH /status { status, expected_version, advocate_name, enrolment_no, note, confirm }
  const submitStatus = async (status) => {
    if (status === "approved" && (!approval.confirm || !approval.name.trim())) return;
    if (status === "approved" && placeholderCount > 0) return warnPlaceholders(placeholderCount);
    const ok = await act(
      `status-${status}`,
      () =>
        setDraftStatus(userId, draftId, {
          status,
          version: draft.version,
          advocateName: approval.name.trim(),
          enrolmentNo: approval.enrolment.trim(),
          note: approval.note.trim(),
          confirm: status === "approved" ? true : undefined,
        }),
      status === "approved" ? "Draft approved." : "Marked as under review.",
      { status }
    );
    if (ok) setTool(null);
  };

  // POST /review { expected_version, note? }
  const sendToAdvocate = async () => {
    const ok = await act(
      "review",
      () => requestReview(userId, draftId, { version: draft.version, note: reviewNote.trim() }),
      "Sent to a JuristBot advocate for review."
    );
    if (ok) {
      setReviewNote("");
      setTool(null);
    }
  };

  const cancelAdvocateReview = () =>
    act("cancel-review", () => cancelReview(userId, draftId, draft.version), "Review request cancelled.");

  const toggleTool = (t) => setTool((cur) => (cur === t ? null : t));
  const statusLabel = String(draft.status || "").replace(/_/g, " ");
  const ap = draft.approval;

  return (
    <div className="pbp-draftview">
      <div className="pbp-meta pbp-row">
        <span className={`pbp-badge pbp-${statusTone()}`}>{v.status_text || v.status || "Draft"}</span>
        <span>
          · {draft.mode} draft · v{draft.version}
          {statusLabel ? ` · ${statusLabel}` : ""}
        </span>
      </div>
      {ap && typeof ap === "object" && (
        <div className="pbp-approved">
          ✓ {String(ap.status || draft.status || "approved").replace(/_/g, " ")}
          {ap.advocate_name && ` by ${ap.advocate_name}`}
          {ap.enrolment_no && ` (${ap.enrolment_no})`}
          {ap.at || ap.approved_at ? ` · ${new Date(ap.at || ap.approved_at).toLocaleString()}` : ""}
          {ap.note && <div className="pbp-muted">{ap.note}</div>}
        </div>
      )}

      {rr?.status === "requested" && (
        <div className="pbp-sent">
          <div>
            <strong>Sent to a JuristBot advocate</strong>
            {rr.requested_at && <span className="pbp-muted"> · {fmtShort(rr.requested_at)}</span>}
          </div>
          <div>You'll get an e-mail when it's reviewed, and this chat is flagged in your list.</div>
          {acts.can_cancel_review && (
            <button
              className="pbp-btn pbp-sent-cancel"
              disabled={busy === "cancel-review"}
              onClick={cancelAdvocateReview}
            >
              {busy === "cancel-review" ? "Cancelling…" : "Cancel request"}
            </button>
          )}
        </div>
      )}

      {rState && rr.status !== "requested" && (
        <div className={`pbp-review pbp-review-${rState.tone}`}>
          <div className="pbp-row pbp-between pbp-top">
            <strong>
              {rState.tone === "wait" ? "⏳ " : rState.tone === "ok" ? "✓ " : "⚠ "}
              {flag?.label || rState.label}
            </strong>
            {rr.round > 1 && <span className="pbp-muted pbp-small">round {rr.round}</span>}
          </div>
          <div className="pbp-small">
            Sent {fmtWhen(rr.requested_at)}
            {rr.draft_version ? ` · draft v${rr.draft_version}` : ""}
            {rr.reviewer ? ` · reviewer: ${typeof rr.reviewer === "object" ? rr.reviewer.name || rr.reviewer.email : rr.reviewer}` : ""}
          </div>
          {rr.user_note && (
            <div className="pbp-small"><em>Your note:</em> {rr.user_note}</div>
          )}
          {rr.decided_at && (
            <div className="pbp-small">Decided {fmtWhen(rr.decided_at)}</div>
          )}
          {rr.reviewer_note && (
            <div className="pbp-review-note"><em>Advocate's note:</em> {rr.reviewer_note}</div>
          )}
          {Array.isArray(rr.log) && rr.log.length > 0 && (
            <>
              <button className="pbp-link" onClick={() => setShowLog((x) => !x)}>
                {showLog ? "Hide history" : `History (${rr.log.length})`}
              </button>
              {showLog && (
                <ul className="pbp-list pbp-small">
                  {rr.log.map((l, i) => (
                    <li key={i}>
                      {fmtWhen(l.at)} — {LOG_TEXT[l.event] || l.event}
                      {l.by && l.by !== "user" ? ` by ${l.by}` : ""}
                      {l.note ? ` · “${l.note}”` : ""}
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      )}

      {/* checks dashboard */}
      {dashboard.length > 0 && (
        <table className="pbp-dash">
          <tbody>
            {dashboard.map((d) => {
              const has = Array.isArray(d.details) && d.details.length > 0;
              const open = openCheck === d.label;
              return (
                <React.Fragment key={d.label}>
                  <tr
                    className={has ? "pbp-clickable" : ""}
                    onClick={() => has && setOpenCheck(open ? null : d.label)}
                  >
                    <td>{d.label}</td>
                    <td className={`pbp-${STATUS_TONE[d.status] || "strong"}-text`}>
                      {d.value}
                      {has && <span className="pbp-caret">{open ? " ▾" : " ▸"}</span>}
                    </td>
                  </tr>
                  {open && (
                    <tr className="pbp-dash-details">
                      <td colSpan={2}>
                        <ul>{d.details.map((x, i) => <li key={i}>{x}</li>)}</ul>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}
      {(conf.data_completeness_pct !== undefined || conf.advocate_review) && (
        <div className="pbp-muted pbp-small">
          Data completeness {conf.data_completeness_pct ?? "—"}% · statutory verification{" "}
          {conf.statutory_verification_pct ?? "—"}% · judgments: {conf.judgment_verification || "—"} · advocate
          review {conf.advocate_review || "—"}
        </div>
      )}

      <div className="pbp-row pbp-wrap pbp-actions-row">
        <button className="pbp-btn" onClick={() => docRef.current?.scrollIntoView({ behavior: "smooth" })}>
          View draft
        </button>
        <button
          className={`pbp-btn${showTerms ? " active" : ""}`}
          onClick={() => {
            setShowTerms((x) => !x);
            setTermInfo(null);
          }}
        >
          Explain legal terms
        </button>
        <button className={`pbp-btn${tool === "opponent" ? " active" : ""}`} onClick={() => toggleTool("opponent")}>
          Check opponent weakness
        </button>
        <button className={`pbp-btn${tool === "judgments" ? " active" : ""}`} onClick={() => toggleTool("judgments")}>
          Find judgments
        </button>
        <button className={`pbp-btn${tool === "improve" ? " active" : ""}`} onClick={() => toggleTool("improve")}>
          Improve draft
        </button>
        {can("can_send_to_advocate") && !acts.can_cancel_review && (
          <button
            className={`pbp-primary pbp-sm${tool === "send" ? " active" : ""}`}
            disabled={busy === "review"}
            onClick={() => toggleTool("send")}
          >
            {busy === "review" ? "Sending…" : "Send to advocate"}
          </button>
        )}
        {can("can_self_approve") && (
          <button className={`pbp-btn${tool === "final" ? " active" : ""}`} onClick={() => toggleTool("final")}>
            Final review
          </button>
        )}
        <button className="pbp-btn" onClick={onBackToFacts}>Back to facts</button>
      </div>

      {tool === "opponent" && (
        <div className="pbp-note pbp-tool">
          <strong>Opposing-counsel review</strong>
          {Array.isArray(review.issues) && review.issues.length > 0 ? (
            <ul className="pbp-list">
              {review.issues.map((x, i) => (
                <li key={i}>
                  {typeof x === "string"
                    ? x
                    : [x.severity && `[${String(x.severity).toUpperCase()}]`, x.issue || x.text || x.summary, x.fix && `— ${x.fix}`]
                        .filter(Boolean)
                        .join(" ") || JSON.stringify(x)}
                </li>
              ))}
            </ul>
          ) : (
            <div>
              {review.ran === false || !review.ran
                ? draft.mode === "quick"
                  ? "Not run in Quick mode."
                  : "Not run for this draft."
                : "No issues found."}
            </div>
          )}
        </div>
      )}

      {tool === "judgments" && (
        <div className="pbp-note pbp-tool">
          <strong>Find judgments</strong>
          <div>Notices carry no case law. Available for complaints and petitions in the next phase.</div>
        </div>
      )}

      {tool === "send" && (
        <div className="pbp-card pbp-tool">
          <div>Send this draft to a JuristBot advocate for review.</div>
          <label className="pbp-field">
            <span>Anything the advocate should know? (optional)</span>
            <input
              className="pbp-input"
              autoFocus
              value={reviewNote}
              onChange={(e) => setReviewNote(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendToAdvocate()}
              disabled={busy === "review"}
            />
          </label>
          <div className="pbp-row">
            <button className="pbp-primary" disabled={busy === "review"} onClick={sendToAdvocate}>
              {busy === "review" ? "Sending…" : "OK"}
            </button>
            <button className="pbp-btn" disabled={busy === "review"} onClick={() => setTool(null)}>Cancel</button>
          </div>
        </div>
      )}

      {tool === "improve" && (
        <div className="pbp-card pbp-tool">
          <strong>Improve this draft</strong>
          <textarea
            className="pbp-textarea"
            rows={3}
            autoFocus
            placeholder="E.g. make the demand paragraph firmer, add the invoice date, shorten the legal position…"
            value={improveText}
            onChange={(e) => setImproveText(e.target.value)}
            disabled={busy === "improve"}
          />
          <label className="pbp-check">
            <input type="checkbox" checked={applyReview} onChange={(e) => setApplyReview(e.target.checked)} />
            Also fix the review notes (For the advocate to check)
          </label>
          {busy === "improve" && <div className="pbp-muted">⏳ Improving… (about 20–40 seconds)</div>}
          <div className="pbp-row">
            <button className="pbp-primary" disabled={!improveText.trim() || busy === "improve"} onClick={submitImprove}>
              {busy === "improve" ? "Improving…" : "Improve draft"}
            </button>
            <button className="pbp-btn" disabled={busy === "improve"} onClick={() => setTool(null)}>Cancel</button>
          </div>
        </div>
      )}

      {tool === "final" && (
        <div className="pbp-card pbp-tool pbp-approval">
          <strong>Advocate approval</strong>
          <div className="pbp-grid2">
            <label>
              <span>Advocate name</span>
              <input
                className="pbp-input"
                value={approval.name}
                onChange={(e) => setApproval({ ...approval, name: e.target.value })}
              />
            </label>
            <label>
              <span>Enrolment no.</span>
              <input
                className="pbp-input"
                value={approval.enrolment}
                onChange={(e) => setApproval({ ...approval, enrolment: e.target.value })}
              />
            </label>
          </div>
          <label>
            <span>Note</span>
            <input
              className="pbp-input"
              value={approval.note}
              onChange={(e) => setApproval({ ...approval, note: e.target.value })}
            />
          </label>
          <label className="pbp-check">
            <input
              type="checkbox"
              checked={approval.confirm}
              onChange={(e) => setApproval({ ...approval, confirm: e.target.checked })}
            />
            I have reviewed this draft as an advocate and approve it for use.
          </label>
          <div className="pbp-row">
            <button
              className="pbp-primary"
              disabled={!approval.confirm || !approval.name.trim() || !!busy}
              onClick={() => submitStatus("approved")}
            >
              {busy === "status-approved" ? "Approving…" : "Approve"}
            </button>
            <button className="pbp-btn" disabled={!!busy} onClick={() => submitStatus("advocate_review")}>
              {busy === "status-advocate_review" ? "Saving…" : "Mark as under review"}
            </button>
          </div>
        </div>
      )}

      {showTerms && termInfo && (
        <div className="pbp-card pbp-term-info">
          <div className="pbp-row pbp-between pbp-top">
            <div>
              <strong>{termInfo.term}</strong>
              {!termInfo.loading && termInfo.label && <span> — {termInfo.label}</span>}
            </div>
            <button className="pbp-btn" onClick={() => setTermInfo(null)}>close</button>
          </div>
          {termInfo.loading ? (
            <div className="pbp-muted">Loading…</div>
          ) : termInfo.meaning ? (
            <div>{termInfo.meaning}</div>
          ) : termInfo.error ? (
            <div className="pbp-danger-text">{termInfo.error}</div>
          ) : termInfo.note ? (
            <div className="pbp-muted">{termInfo.note}</div>
          ) : termInfo.missing ? (
            <div className="pbp-muted">Not in the terminology register yet.</div>
          ) : null}
          {!termInfo.loading && termInfo.plain && <div className="pbp-muted">In plain words: {termInfo.plain}</div>}
          {!termInfo.loading && termInfo.why && (
            <div className="pbp-small"><em>Why it's used here:</em> {termInfo.why}</div>
          )}
        </div>
      )}

      {showTerms && terms.length > 0 && (
        <div className="pbp-card pbp-terms">
          <div><strong>Legal terms in this draft</strong> <span className="pbp-muted">(tap one)</span></div>
          <div className="pbp-chips">
            {terms.map((t) => (
              <button
                key={t.term}
                className={`pbp-chip${termInfo?.term === t.term ? " active" : ""}`}
                onClick={() => openTerm(t)}
              >
                {t.term} · {t.category}
              </button>
            ))}
          </div>
        </div>
      )}

      {reviewItems.length > 0 && (
        <section>
          <h6 className="pbp-h">For the advocate to check</h6>
          <ul className="pbp-list">{reviewItems.map((x, i) => <li key={i}>{x}</li>)}</ul>
        </section>
      )}

      {/* the document */}
      <section ref={docRef}>
        <h6 className="pbp-h">Draft</h6>
        <div className="pbp-paper">
          {draft.title && <div className="pbp-paper-title">{draft.title}</div>}
          {sections.map((s) => (
            <div key={s.key} className={`pbp-sec${editKey === s.key ? " editing" : ""}`}>
              {editKey === s.key ? (
                <>
                  {s.heading && <div className="pbp-sec-heading">{s.heading}</div>}
                  <textarea
                    className="pbp-textarea"
                    rows={Math.max(4, editText.split("\n").length + 1)}
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                  />
                  <div className="pbp-muted pbp-small">Leave a blank line between paragraphs.</div>
                  <div className="pbp-row">
                    <button className="pbp-primary" disabled={saving} onClick={saveEdit}>
                      {saving ? "Saving…" : "Save"}
                    </button>
                    <button className="pbp-btn" onClick={() => setEditKey(null)}>Cancel</button>
                  </div>
                </>
              ) : (
                <>
                  <button className="pbp-btn pbp-sec-edit" onClick={() => startEdit(s)}>Edit</button>
                  {s.heading && <div className="pbp-sec-heading">{s.heading}</div>}
                  {(s.paragraphs || []).map((p, i) => <p key={i}>{withPlaceholders(p)}</p>)}
                  {s.edited_by_user && <span className="pbp-badge pbp-info">edited</span>}
                </>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* facts, law and versions */}
      <details className="pbp-used">
        <summary>Facts, law and versions used ({factsUsed.length} facts)</summary>
        <ul className="pbp-list">
          {factsUsed.map((f) => (
            <li key={f.fact_id}>
              {f.label}: {f.value} <span className="pbp-src">({sourceText(f)})</span>
              {f.quote && f.source_type === "uploaded_document" && <em className="pbp-quote">“{f.quote}”</em>}
            </li>
          ))}
          {statutes.map((s) => (
            <li key={`${s.act}-${s.section}`}>
              {s.short || `s.${s.section} ${s.act}`} — {s.status}
              {s.note && <span className="pbp-src"> · {s.note}</span>}
            </li>
          ))}
        </ul>
        {draft.versions?.playbook && (
          <div className="pbp-muted pbp-small">
            Playbook {draft.versions.playbook.id} v{draft.versions.playbook.version}
            {draft.versions.prompts?.prompt_version && ` · prompts ${draft.versions.prompts.prompt_version}`}
            {draft.versions.engine && ` · ${draft.versions.engine}`}
            {draft.matter_version && ` · matter v${draft.matter_version}`}
          </div>
        )}
      </details>
    </div>
  );
};

export default PlaybookDraftView;